import { makeArticle } from "./learnFactory";
import type { LearnFAQ } from "./learnShared";

const acceleratedTmsRelated = [
  {
    title: "SAINT® vs Accelerated TMS",
    href: "/learn/saint-vs-accelerated-tms",
    description:
      "What's the same and what isn't between SAINT® and non-SAINT accelerated protocols",
  },
  {
    title: "SAINT® vs One-Day TMS",
    href: "/learn/saint-vs-one-day-tms",
    description: "How a 5-day protocol compares to a single-day approach",
  },
  {
    title: "Accelerated TMS & Theta Burst",
    href: "/learn/accelerated-tms-theta-burst-stimulation",
    description: "How iTBS pulse patterns power accelerated schedules",
  },
  {
    title: "SAINT® TMS Cost",
    href: "/learn/saint-tms-cost",
    description: "Price, insurance, Medicare, and financing",
  },
  {
    title: "SAINT® TMS Locations",
    href: "/learn/saint-tms-locations",
    description: "Verified providers and how to confirm a real SAINT® clinic",
  },
  {
    title: "BEAM F3 vs fMRI targeting",
    href: "/learn/beam-f3-vs-fmri-tms",
    description:
      "How head-measurement targeting differs from an fMRI-guided target",
  },
  {
    title: "Schedule a Consultation",
    href: "/contactus",
    description: "Talk with our clinical team about which protocol may fit you",
  },
];

const acceleratedTmsFaqs: LearnFAQ[] = [
  {
    question: "What is accelerated TMS?",
    answer:
      "Accelerated TMS is any TMS protocol that delivers more than one session per day, compressing a course that normally takes four to six weeks into roughly one to two weeks. Several protocols exist, and they are not all the same. The only accelerated protocol with its own FDA clearance is SAINT® on the Magnus Neuromodulation System, which uses fMRI-guided personalized targeting. Other accelerated schedules are off-label uses of FDA-cleared devices.",
  },
  {
    question: "Is accelerated TMS better than regular TMS?",
    answer:
      "Not established. SAINT® showed promising results in small studies — 79% of participants in the 2022 randomized trial reached remission (Cole et al., Am J Psychiatry 2022;179(2):132–141). Individual results may vary. Conventional TMS has a larger evidence base and is more often covered by insurance. The two have never been compared head-to-head. The right choice depends on your clinical history and circumstances.",
  },
  {
    question: "How much does accelerated TMS cost?",
    answer:
      "Cost depends on the protocol. SAINT® at Neuro Wellness starts at $25,000, which includes the fMRI scan, personalized targeting, 50 sessions over five days, physician oversight, and follow-up. Lower-priced accelerated protocols may use a conventional device without the SAINT® targeting system. SAINT® may be covered; coverage varies by plan. We verify your benefits for free. Financing is available on a case-by-case basis and discussed during consultation.",
  },
  {
    question: "Where can I get accelerated TMS in Florida?",
    answer:
      "Neuro Wellness TMS Centers of America is a Verified SAINT® Provider in Coral Springs, Florida, at 9734 W Sample Rd, 2nd Floor, Suite B. The clinic is in Broward County, about 20 minutes from Fort Lauderdale and within reach of Palm Beach County and Miami-Dade County. Patients travel in from out of state for the five-day format; we support travel logistics. Call 954-827-9793 to confirm availability.",
  },
  {
    question: "Does the 79% remission rate apply to all accelerated TMS?",
    answer:
      "No. The 79% remission figure comes from the SAINT® clinical trials, which used the Magnus system with fMRI-guided targeting. Those results have not been replicated for non-SAINT accelerated protocols. Ask any clinic offering accelerated TMS for its own published outcome data for the specific protocol and device it uses.",
  },
  {
    question: "Can I do accelerated TMS if standard TMS didn't work?",
    answer:
      "Possibly. SAINT® uses fMRI-guided personalized targeting, which differs from the scalp-measurement method used in standard TMS. Whether an accelerated protocol is appropriate after standard TMS is a clinical decision that depends on your history. SAINT® is FDA-cleared for adults with major depressive disorder whose depression hasn't improved enough on prior antidepressant medication. A consultation can help determine whether it may fit your situation.",
  },
];

export const pageWhatIsAcceleratedTms = makeArticle({
  slug: "what-is-accelerated-tms",
  title: "Accelerated TMS: Complete Guide (SAINT®, iTBS, ONE-D) | Florida",
  description:
    "Accelerated TMS compresses weeks of treatment into days. Compare SAINT®, accelerated iTBS and one-day protocols on evidence, cost, and who qualifies.",
  canonical: "/what-is-accelerated-tms",
  h1: "Accelerated TMS: The Complete Guide",
  breadcrumbLabel: "What Is Accelerated TMS",
  shortAnswer:
    "Accelerated TMS is any TMS protocol that delivers more than one session per day, compressing a four-to-six-week course into roughly one to two weeks. Several protocols exist, and they are not all the same. The only accelerated protocol with its own FDA clearance is SAINT® on the Magnus Neuromodulation System, which uses fMRI-guided personalized targeting. Other accelerated schedules are off-label uses of FDA-cleared devices.",
  keyTakeaways: [
    "Accelerated TMS is a schedule — multiple sessions per day — not a single treatment.",
    "Only SAINT® (Magnus Neuromodulation System, K220177) has its own FDA clearance as an accelerated protocol.",
    "Other accelerated schedules (accelerated iTBS, ONE-D, accelerated rTMS) are off-label uses of FDA-cleared devices.",
    "SAINT® uses fMRI-guided personalized targeting; most other accelerated protocols do not.",
    "SAINT® at Neuro Wellness starts at $25,000; coverage varies by plan, and we verify benefits for free.",
  ],
  sections: [
    {
      heading:
        "What 'Accelerated' Means — and Why Not All Accelerated TMS Is the Same",
      paragraphs: [
        "Traditional transcranial magnetic stimulation (TMS) delivers one session per day, five days a week, for about four to six weeks — roughly 36 sessions total. Accelerated TMS changes the schedule: it delivers multiple sessions per day with breaks in between, compressing the whole course into roughly one to two weeks. The daily time commitment is much larger, but the overall duration is much shorter.",
        "The word 'accelerated' describes the schedule, not a single treatment. Several different accelerated protocols exist, and they differ in how they target the brain, what device they use, and what evidence supports them. Understanding those differences matters when comparing options. The most studied accelerated protocol is SAINT® (Stanford Accelerated Intelligent Neuromodulation Therapy), which is FDA-cleared and uses fMRI-guided personalized targeting on the Magnus Neuromodulation System.",
      ],
    },
    {
      heading: "Types of Accelerated TMS",
      paragraphs: [
        "Accelerated TMS is an umbrella term. The protocols below all deliver multiple sessions per day, but they are not interchangeable.",
      ],
    },
    {
      heading: "SAINT® / SNT",
      paragraphs: [
        "SAINT® (also called SNT, Stanford Neuromodulation Therapy) is a specific, FDA-cleared accelerated protocol delivered on the Magnus Neuromodulation System. It uses an fMRI scan to individually map each person's treatment target — a personalized approach rather than a one-size-fits-all scalp measurement. The schedule is 10 sessions per day for 5 consecutive days, about 50 sessions total, using intermittent theta burst stimulation (iTBS). SAINT® received FDA clearance (K220177) for major depressive disorder in adults whose depression hasn't improved enough on prior antidepressant medication, and it held the FDA's Breakthrough Device designation during development.",
      ],
    },
    {
      heading: "Accelerated iTBS Without Personalized Targeting",
      paragraphs: [
        "Some clinics deliver accelerated schedules using intermittent theta burst stimulation (iTBS) on a conventional TMS device without fMRI-guided targeting. iTBS is a pulse pattern that delivers stimulation in short, high-frequency bursts; it is FDA-cleared as a treatment pattern, but using it in an accelerated, multi-session-per-day schedule is an off-label use of the cleared device. These protocols can copy the compressed schedule but do not use the SAINT® targeting software or the Magnus Neuromodulation System.",
      ],
    },
    {
      heading: "ONE-D (One-Day) Protocols",
      paragraphs: [
        "ONE-D and similar one-day protocols attempt to compress treatment into a single day. These are the most aggressive accelerated schedules. They use FDA-cleared devices off-label, and the evidence base is smaller and less mature than for SAINT®. Because the entire course happens in one day, the practical and clinical considerations differ from a five-day protocol.",
      ],
    },
    {
      heading: "Accelerated Standard rTMS",
      paragraphs: [
        "Some clinics simply deliver standard repetitive TMS (rTMS) more frequently — for example, twice a day — rather than using theta burst stimulation. This is another off-label use of an FDA-cleared device. The evidence for this approach is limited compared with both conventional TMS and SAINT®.",
      ],
    },
    {
      heading: "Evidence by Type",
      paragraphs: [
        "The evidence behind each accelerated protocol varies. The table below summarizes what is known. Note that the strongest accelerated evidence belongs to SAINT® specifically; results from SAINT® trials do not transfer to other accelerated protocols.",
      ],
      table: {
        headers: [
          "Protocol",
          "Sessions/day",
          "Days",
          "Personalized target?",
          "FDA status of targeting",
          "Key trial",
          "Remission data",
        ],
        rows: [
          [
            "SAINT® / SNT (Magnus)",
            "10",
            "5",
            "Yes — fMRI-guided",
            "FDA-cleared (K220177)",
            "Cole et al., 2022 RCT",
            "79% remission in the 2022 trial. Individual results may vary.",
          ],
          [
            "Accelerated iTBS (non-SAINT)",
            "Varies",
            "1–2 weeks",
            "Usually no",
            "Off-label use of cleared device",
            "Small studies, no large RCT for this exact schedule",
            "No published remission rate for this exact protocol",
          ],
          [
            "ONE-D (one-day)",
            "Many",
            "1",
            "Varies",
            "Off-label use of cleared device",
            "Very limited",
            "Limited published data",
          ],
          [
            "Accelerated standard rTMS",
            "2+",
            "1–3 weeks",
            "No",
            "Off-label use of cleared device",
            "Limited",
            "Limited published data",
          ],
          [
            "Conventional TMS (for comparison)",
            "1",
            "4–6 weeks",
            "No (scalp measurement)",
            "FDA-cleared",
            "Multiple RCTs",
            "Established; larger evidence base",
          ],
        ],
      },
    },
    {
      heading: "What a Week Looks Like",
      paragraphs: [
        "For SAINT®, the week is intensive. A typical day includes 10 treatment sessions, each lasting a few minutes, with breaks spaced throughout the day. The full course runs five consecutive days. Because the schedule is compressed, many patients travel in for the week rather than commuting daily for six weeks. A support person can accompany you. The compressed format is one reason a five-day protocol makes travel practical for out-of-state patients.",
        "Other accelerated protocols vary. Accelerated iTBS schedules may run one to two weeks; ONE-D compresses everything into a single day. The daily commitment and total duration depend on the specific protocol.",
      ],
    },
    {
      heading: "Who's a Candidate",
      paragraphs: [
        "SAINT® is FDA-cleared for adults with major depressive disorder (MDD) whose depression hasn't improved enough on prior antidepressant medication. Neuro Wellness treats adults 22 and older. SAINT® is not cleared for anxiety, OCD, PTSD, bipolar disorder, concussion, or any other condition, and this page does not imply it treats those conditions. Never target minors.",
        "Whether an accelerated protocol is appropriate is a clinical decision that depends on your history, the severity of your depression, prior treatments, and your schedule. A consultation can help determine whether SAINT® or another approach may fit your situation. You can start with our [[am-i-a-candidate-screener|free candidacy screener]].",
      ],
    },
    {
      heading: "Cost and Insurance",
      paragraphs: [
        "SAINT® at Neuro Wellness starts at $25,000. That price includes the fMRI scan, personalized targeting, 50 sessions over five days, physician oversight, and follow-up. Financing is available on a case-by-case basis and discussed during consultation. Lower-priced accelerated protocols may use a conventional device without the SAINT® targeting system; the trade-off is that they do not use the FDA-cleared SAINT® targeting software, and the SAINT® trial outcomes do not transfer to them.",
        "SAINT® may be covered; coverage varies by plan. SAINT uses Category III CPT codes, and some insurers do not yet cover it, so it is often self-pay. We verify your benefits for free, whatever carrier you have, and we confirm what your plan allows before you commit. Conventional TMS is more often covered by insurance. You can [[check-insurance-options|check your insurance options]] or learn more about [[saint-tms-cost|SAINT® TMS cost]].",
      ],
    },
    {
      heading: "Finding Accelerated TMS Near You: South Florida and Travel-In",
      paragraphs: [
        "If you're searching for accelerated TMS near you, the most important step is confirming exactly which protocol a clinic offers. Neuro Wellness TMS Centers of America is a Verified SAINT® Provider in Coral Springs, Florida, at 9734 W Sample Rd, 2nd Floor, Suite B. The clinic is in Broward County, about 20 minutes from Fort Lauderdale and within reach of Palm Beach County and Miami-Dade County.",
        "Because SAINT® is a five-day intensive, patients travel in from out of state. The compressed schedule makes a week-long trip practical compared with six weeks of daily conventional TMS. Nearby airports include Fort Lauderdale–Hollywood International (FLL), Palm Beach International (PBI), and Miami International (MIA). We support travel logistics and can share recommendations on nearby lodging when you book your consultation. Call 954-827-9793 to confirm availability and discuss travel.",
        "To verify a clinic offers real SAINT®, ask whether it uses the Magnus Neuromodulation System with SAINT® Technology specifically. Red flags include language like 'SAINT-like,' 'Stanford protocol,' or no mention of Magnus. See our [[saint-tms-locations|SAINT® TMS locations]] guide for more.",
      ],
    },
    {
      heading: "Comparison Table",
      paragraphs: [
        "The table below summarizes the key differences across accelerated protocols and conventional TMS.",
      ],
      table: {
        headers: [
          "Protocol",
          "Sessions/day",
          "Days",
          "Personalized target?",
          "FDA status of targeting",
          "Key trial",
          "Remission data",
        ],
        rows: [
          [
            "SAINT® (Magnus)",
            "10",
            "5",
            "Yes — fMRI-guided",
            "FDA-cleared (K220177)",
            "Cole et al., 2022 RCT",
            "79% remission. Individual results may vary.",
          ],
          [
            "Accelerated iTBS (non-SAINT)",
            "Varies",
            "1–2 weeks",
            "Usually no",
            "Off-label",
            "Small studies",
            "No published remission rate",
          ],
          [
            "ONE-D (one-day)",
            "Many",
            "1",
            "Varies",
            "Off-label",
            "Very limited",
            "Limited published data",
          ],
          [
            "Accelerated rTMS",
            "2+",
            "1–3 weeks",
            "No",
            "Off-label",
            "Limited",
            "Limited published data",
          ],
          [
            "Conventional TMS",
            "1",
            "4–6 weeks",
            "No",
            "FDA-cleared",
            "Multiple RCTs",
            "Established",
          ],
        ],
      },
    },
  ],
  faqs: acceleratedTmsFaqs,
  sources: [
    {
      citation:
        "Cole EJ, Stimpson KH, Bentzley BS, et al. Stanford Neuromodulation Therapy (SNT): A Double-Blind Randomized Controlled Trial. Am J Psychiatry. 2022;179(2):132–141.",
      url: "https://pubmed.ncbi.nlm.nih.gov/35624717/",
    },
    {
      citation:
        "Cole EJ, Phillips AL, Bentzley BS, et al. Stanford Accelerated Intelligent Neuromodulation Therapy for treatment-resistant depression. Am J Psychiatry. 2020;177(8):716–726.",
      url: "https://pubmed.ncbi.nlm.nih.gov/32472615/",
    },
    {
      citation:
        "U.S. FDA. 510(k) Premarket Notification K220177. Magnus Neuromodulation System. September 2022.",
      url: "https://www.accessdata.fda.gov/scripts/cdrh/cfdocs/cfpmn/pmn.cfm?ID=K220177",
    },
    {
      citation:
        "Magnus Medical. SAINT® Neuromodulation System — Breakthrough Device designation and FDA clearance.",
      url: "https://www.magnusmedical.com",
    },
  ],
  about: [
    {
      "@type": "MedicalTherapy",
      name: "Accelerated Transcranial Magnetic Stimulation",
    },
    {
      "@type": "MedicalTherapy",
      name: "SAINT® Neuromodulation System",
    },
  ],
  relatedLinks: acceleratedTmsRelated,
  reviewBlock: {
    writtenBy: "Neuro Wellness TMS Centers of America",
    reviewedBy: "Daniel Pietras, MD",
    reviewedByHref: "/dr-daniel-pietras-md",
    published: "November 3, 2026",
    lastReviewed: "November 3, 2026",
    lastUpdated: "November 3, 2026",
  },
  toc: [
    { label: "Quick Answer", href: "#quick-answer" },
    {
      label: "What 'Accelerated' Means",
      href: "#what-accelerated-means-and-why-not-all-accelerated-tms-is-the-same",
    },
    { label: "Types of Accelerated TMS", href: "#types-of-accelerated-tms" },
    { label: "Evidence by Type", href: "#evidence-by-type" },
    { label: "What a Week Looks Like", href: "#what-a-week-looks-like" },
    { label: "Who's a Candidate", href: "#whos-a-candidate" },
    { label: "Cost and Insurance", href: "#cost-and-insurance" },
    {
      label: "Finding Accelerated TMS Near You",
      href: "#finding-accelerated-tms-near-you-south-florida-and-travel-in",
    },
    { label: "Comparison Table", href: "#comparison-table" },
    { label: "FAQ", href: "#faq" },
    { label: "Cited Research", href: "#cited-research" },
  ],
  safetyBlock:
    "SAINT® is not for everyone. It is contraindicated for people with non-removable magnetic-sensitive metal in or near the head, certain implanted devices, or increased intracranial pressure. Possible side effects include headache, fatigue, scalp discomfort and anxiety; TMS carries a small risk of seizure and, as with any depression treatment, a risk of worsening depression or suicidal thoughts. If you are in crisis, call or text 988.",
  trademarkFooter:
    "SAINT® is a registered trademark of Stanford University, exclusively licensed to Magnus Medical, Inc. NWCOA is not affiliated with Stanford University.",
  ctaBand: {
    headline: "See if SAINT® is a fit, free",
    primaryText: "Learn If It's a Fit",
    primaryHref: "/am-i-a-candidate-screener",
    secondaryText: "Check my insurance",
    secondaryHref: "/check-insurance-options",
    phone: "954-827-9793",
  },
});
